import type { ComponentType, ReactNode } from 'react'
import React from 'react'
import LandingPage from '../pages/LandingPage'
import RegisterPage from '../features/auth/RegisterPage'
import ProtectedRoute from '../features/auth/ProtectedRoute'
import { LessonRouteGuard } from '@/design-system/organisms/LessonRouteGuard'
import AdminRouteGuard from '../features/auth/AdminRouteGuard'

// Lazy-loaded route components (code-split into separate chunks).
// LandingPage and RegisterPage stay static: LandingPage is the highest-traffic
// entry route, and RegisterPage is the heaviest public page whose LCP can't
// afford the extra round-trip a lazy chunk costs (TRA-115). Login and privacy
// load their own small chunks on demand.
const LoginPage = React.lazy(() => import('../features/auth/LoginPage'))
const PrivacyPolicyPage = React.lazy(() => import('../pages/PrivacyPolicyPage'))

/**
 * Starts downloading the lazy chunk for a public route as soon as the entry
 * module executes, so a direct visit to e.g. /login doesn't pay a serial
 * round-trip after the router mounts. The import() here and the React.lazy
 * factory above resolve to the same module request, so the fetch is shared.
 * Returns the in-flight import so callers (tests) can await settlement.
 */
export function warmPublicRouteChunk(pathname: string): Promise<unknown> | undefined {
    if (pathname === '/login') {
        return import('../features/auth/LoginPage')
    }
    if (pathname === '/privacy') {
        return import('../pages/PrivacyPolicyPage')
    }
    return undefined
}
const DashboardPage = React.lazy(() => import('../features/dashboard/DashboardPageContainer'))
const LessonPage = React.lazy(() => import('../features/lessons/LessonPageContainer'))
const ReaderPage = React.lazy(() => import('../features/reader/ReaderPageContainer'))
const ProfileSettingsPage = React.lazy(
    () => import('../features/profile/ProfileSettingsPageContainer')
)
const ReviewPage = React.lazy(() => import('../features/review/ReviewPageContainer'))
const PersonalReviewPage = React.lazy(() => import('../features/review/PersonalReviewPage'))
const MyVocabularyPage = React.lazy(
    () => import('../features/vocabulary/MyVocabularyPageContainer')
)
const AdminRedirectPage = React.lazy(() => import('../pages/admin/AdminRedirectPage'))
const AdminContentPage = React.lazy(() => import('../pages/admin/AdminContentPage'))
const AdminCorpusDetailPage = React.lazy(
    () => import('../pages/admin/content/AdminCorpusDetailPage')
)
const AdminStoryEditorPage = React.lazy(() => import('../pages/admin/content/AdminStoryEditorPage'))
const AdminFeedbackPage = React.lazy(() => import('../pages/admin/AdminFeedbackPage'))
const AdminUsersPage = React.lazy(() => import('../pages/admin/AdminUsersPage'))
const AdminLogsPage = React.lazy(() => import('../pages/admin/AdminLogsPage'))
const AdminSettingsPage = React.lazy(() => import('../pages/admin/AdminSettingsPage'))
const AccountInactivePage = React.lazy(() => import('../pages/AccountInactivePage'))
const AdminInviteKeysPage = React.lazy(() => import('../pages/admin/AdminInviteKeysPage'))
const NotFoundPage = React.lazy(() => import('../pages/NotFoundPage'))

/**
 * Layout type for route configuration.
 * Determines which layout component wraps the route.
 */
export type LayoutType = 'core' | 'auth' | 'lesson' | 'admin'

/**
 * Route metadata interface for SEO and page titles.
 * All metadata uses i18n translation keys.
 */
export interface RouteMetadata {
    /** i18n key for page title (e.g., 'seo:landing.title') */
    titleKey: string
    /** i18n key for meta description (e.g., 'seo:landing.description') */
    descriptionKey?: string
    /** Canonical URL path (defaults to route path) */
    canonicalPath?: string
}

/**
 * Route configuration interface.
 * Defines all properties needed for route setup.
 */
export interface RouteConfig {
    /** Route path (e.g., '/', '/dashboard') */
    path: string
    /** React component to render for this route */
    component: ComponentType | React.LazyExoticComponent<ComponentType>
    /** Whether this route requires authentication */
    requiresAuth: boolean
    /** Whether this route requires an LLM API key */
    requiresLLMKey: boolean
    /** Layout type to use for this route */
    layout: LayoutType
    /** Route metadata for SEO (optional, will be added in Phase 3) */
    metadata?: RouteMetadata
    /** Array of role IDs required to access this route (e.g., ['support', 'admin']) */
    requiresRole?: string[]
}

/**
 * Route configuration array.
 * Centralizes all route definitions for easy maintenance and readability.
 *
 * Routes are organized by:
 * - Public routes (no auth required)
 * - Protected routes (auth required)
 * - Lesson routes (auth + LLM API key required)
 */
export const routes: RouteConfig[] = [
    // Public routes
    {
        path: '/',
        component: LandingPage,
        requiresAuth: false,
        requiresLLMKey: false,
        layout: 'core',
        metadata: {
            titleKey: 'seo:landing.title',
            descriptionKey: 'seo:landing.description',
        },
    },
    {
        path: '/login',
        component: LoginPage,
        requiresAuth: false,
        requiresLLMKey: false,
        layout: 'core',
        metadata: {
            titleKey: 'seo:login.title',
            descriptionKey: 'seo:login.description',
        },
    },
    {
        path: '/register',
        component: RegisterPage,
        requiresAuth: false,
        requiresLLMKey: false,
        layout: 'core',
        metadata: {
            titleKey: 'seo:register.title',
            descriptionKey: 'seo:register.description',
        },
    },
    {
        path: '/privacy',
        component: PrivacyPolicyPage,
        requiresAuth: false,
        requiresLLMKey: false,
        layout: 'core',
        metadata: {
            titleKey: 'seo:privacyPolicy.title',
            descriptionKey: 'seo:privacyPolicy.description',
            canonicalPath: '/privacy',
        },
    },
    // Protected routes (auth required)
    {
        path: '/dashboard',
        component: DashboardPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'auth',
        metadata: {
            titleKey: 'seo:dashboard.title',
            descriptionKey: 'seo:dashboard.description',
        },
    },
    {
        path: '/review',
        component: ReviewPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'auth',
        metadata: {
            titleKey: 'seo:review.title',
            descriptionKey: 'seo:review.description',
        },
    },
    {
        path: '/review/personal',
        component: PersonalReviewPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'auth',
        metadata: {
            titleKey: 'seo:personalReview.title',
            descriptionKey: 'seo:personalReview.description',
        },
    },
    {
        path: '/vocabulary',
        component: MyVocabularyPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'auth',
        metadata: {
            titleKey: 'seo:vocabulary.title',
            descriptionKey: 'seo:vocabulary.description',
        },
    },
    {
        path: '/profile',
        component: ProfileSettingsPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'auth',
        metadata: {
            titleKey: 'seo:profile.title',
            descriptionKey: 'seo:profile.description',
        },
    },
    // Lesson route (auth + LLM API key required)
    {
        path: '/lesson',
        component: LessonPage,
        requiresAuth: true,
        requiresLLMKey: true,
        layout: 'lesson',
        metadata: {
            titleKey: 'seo:lesson.title',
            descriptionKey: 'seo:lesson.description',
        },
    },
    // Reader route (auth + LLM API key required)
    {
        path: '/reader',
        component: ReaderPage,
        requiresAuth: true,
        requiresLLMKey: true,
        layout: 'lesson',
        metadata: {
            titleKey: 'seo:reader.title',
            descriptionKey: 'seo:reader.description',
        },
    },
    // Account inactive route (auth required, subscription-gated users land here)
    {
        path: '/account-inactive',
        component: AccountInactivePage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'core',
        metadata: {
            titleKey: 'seo:accountInactive.title',
            descriptionKey: 'seo:accountInactive.description',
        },
    },
    // Admin routes (auth + role required)
    {
        path: '/admin',
        component: AdminRedirectPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
    },
    {
        path: '/admin/content',
        component: AdminContentPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:content.title',
        },
    },
    {
        path: '/admin/content/corpora/:corpusId',
        component: AdminCorpusDetailPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:content.corpora.title',
        },
    },
    {
        path: '/admin/content/stories/:storyId',
        component: AdminStoryEditorPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:content.editor.title',
        },
    },
    {
        path: '/admin/feedback',
        component: AdminFeedbackPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:feedback.title',
        },
    },
    {
        path: '/admin/users',
        component: AdminUsersPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:users.title',
        },
    },
    {
        path: '/admin/invite-keys',
        component: AdminInviteKeysPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['support', 'admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:inviteKeys.title',
        },
    },
    {
        path: '/admin/logs',
        component: AdminLogsPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['admin', 'platform_owner'],
        metadata: {
            titleKey: 'admin:logs.title',
        },
    },
    {
        path: '/admin/settings',
        component: AdminSettingsPage,
        requiresAuth: true,
        requiresLLMKey: false,
        layout: 'admin',
        requiresRole: ['platform_owner'],
        metadata: {
            titleKey: 'admin:settings.title',
        },
    },
    // Catch-all 404 route (must be last so it only matches unknown URLs)
    {
        path: '*',
        component: NotFoundPage,
        requiresAuth: false,
        requiresLLMKey: false,
        layout: 'core',
        metadata: {
            titleKey: 'seo:notFound.title',
            descriptionKey: 'seo:notFound.description',
        },
    },
]

/**
 * Helper function to create route element with appropriate guards.
 * This function wraps components with ProtectedRoute, LessonRouteGuard, and AdminRouteGuard as needed.
 */
export function createRouteElement(config: RouteConfig): ReactNode {
    const { component: Component, requiresAuth, requiresLLMKey, requiresRole } = config

    let element: ReactNode = React.createElement(Component as ComponentType)

    // Apply route guards based on requirements
    // Order matters: AdminRouteGuard (role check) must be inside ProtectedRoute (auth check)
    if (requiresLLMKey) {
        // Lesson routes: auth + LLM API key
        element = React.createElement(ProtectedRoute, {
            children: React.createElement(LessonRouteGuard, { children: element }),
        })
    } else if (requiresAuth) {
        // Protected routes: auth required
        if (requiresRole && requiresRole.length > 0) {
            // Admin routes: auth + role check
            element = React.createElement(ProtectedRoute, {
                children: React.createElement(AdminRouteGuard, {
                    requiredRoles: requiresRole,
                    children: element,
                }),
            })
        } else {
            // Regular protected routes: auth only
            element = React.createElement(ProtectedRoute, { children: element })
        }
    }

    return element
}
